import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { ReportData } from '../../../types/report.types';
import { renderPdfElements, PdfElement } from '../../pdfHtmlImageUtils';

interface ExecutiveSummaryProps {
  reportData: ReportData;
  pieChartImage?: string;
  barChartImage?: string;
  sectionId?: string;
}

const severityColors: Record<string, string> = {
  Critical: '#B91C1C',
  High: '#EA580C',
  Medium: '#EAB308',
  Low: '#16A34A',
  Info: '#64748B',
};

// HTML Parser helper
function htmlToParagraphs(html: string): string[] {
  if (!html) return [];
  let withBreaks = html.replace(/<br\s*\/?>/gi, '\n');
  withBreaks = withBreaks.replace(/<\/?(p|div)[^>]*>/gi, '\n');
  const tmp = typeof document !== 'undefined' ? document.createElement('div') : null;
  if (tmp) {
    tmp.innerHTML = withBreaks;
    const text = tmp.textContent || tmp.innerText || '';
    return text.split(/\n+/).map(s => s.trim()).filter(Boolean);
  }
  return withBreaks.replace(/<[^>]*>/g, '').split(/\n+/).map(s => s.trim()).filter(Boolean);
}

// A helper to extract bullets from HTML
function htmlToBullets(html: string): string[] {
  if (!html) return [];
  const liMatches = html.match(/<li[^>]*>(.*?)<\/li>/gi);
  if (liMatches) {
    return liMatches.map(li => {
      return li
        .replace(/<[^>]*>/g, '') 
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .trim();
    }).filter(Boolean);
  }
  return htmlToParagraphs(html);
}

function parseHtmlToElementsSync(htmlString: string): PdfElement[] {
  if (!htmlString) return [];
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return [{ type: 'text', content: htmlString.replace(/<[^>]+>/g, '') }];
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const elements: PdfElement[] = [];

  const processNode = (node: Node, container: PdfElement[] = elements, activeStyles = ''): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const raw = node.textContent ?? '';
      const normalized = raw.replace(/\s+/g, ' ');
      if (normalized.trim().length > 0) {
        container.push({ type: 'text', content: normalized, style: activeStyles || undefined });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      let newStyles = activeStyles;
      const styleAttr = el.getAttribute('style') || '';
      if (styleAttr) {
        if (/text-decoration(-line)?:\s*underline/i.test(styleAttr)) {
          newStyles = (newStyles ? newStyles + ' ' : '') + 'underline';
        }
        if (/text-decoration(-line)?:\s*line-through/i.test(styleAttr)) {
          newStyles = (newStyles ? newStyles + ' ' : '') + 'strikethrough';
        }
        if (/font-weight:\s*(bold|700)/i.test(styleAttr)) {
          newStyles = (newStyles ? newStyles + ' ' : '') + 'bold';
        }
        if (/font-style:\s*italic/i.test(styleAttr)) {
          newStyles = (newStyles ? newStyles + ' ' : '') + 'italic';
        }
      }

      switch (el.tagName.toLowerCase()) {
        case 'p': {
          const paragraph: PdfElement = { type: 'paragraph', children: [] };
          for (const child of Array.from(el.childNodes)) {
            processNode(child, paragraph.children, newStyles);
          }
          if (paragraph.children.length > 0) {
            container.push(paragraph);
          }
          break;
        }
        case 'strong':
        case 'b': {
          const combinedStyles = (newStyles ? newStyles + ' ' : '') + 'bold';
          for (const child of Array.from(el.childNodes)) {
            processNode(child, container, combinedStyles);
          }
          break;
        }
        case 'em':
        case 'i': {
          const combinedStyles = (newStyles ? newStyles + ' ' : '') + 'italic';
          for (const child of Array.from(el.childNodes)) {
            processNode(child, container, combinedStyles);
          }
          break;
        }
        case 'u': {
          const combinedStyles = (newStyles ? newStyles + ' ' : '') + 'underline';
          for (const child of Array.from(el.childNodes)) {
            processNode(child, container, combinedStyles);
          }
          break;
        }
        case 'li': {
          const paragraph: PdfElement = { type: 'paragraph', children: [] };
          for (const child of Array.from(el.childNodes)) {
            processNode(child, paragraph.children, newStyles);
          }
          container.push(paragraph);
          break;
        }
        default: {
          for (const child of Array.from(el.childNodes)) {
            processNode(child, container, newStyles);
          }
          break;
        }
      }
    }
  };

  for (const child of Array.from(doc.body.childNodes)) {
    processNode(child);
  }

  return elements;
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: '16mm 14mm',
    paddingBottom: '22mm', // Leave extra room for footer page number
    fontFamily: 'Roboto',
    fontSize: 9.5,
    color: '#4B5563',
    position: 'relative',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    fontSize: 8.5,
    color: '#9CA3AF',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  footerLeft: {
    textAlign: 'left',
  },
  footerRight: {
    textAlign: 'right',
  },
  confidentialityBox: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: '8pt 10pt',
    marginBottom: 16,
  },
  confidentialityText: {
    fontSize: 8,
    color: '#9CA3AF',
    lineHeight: 1.4,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0F172A',
  },
  titleUnderlineRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    width: '100%',
    height: 3,
    marginTop: 4,
    marginBottom: 16,
  },
  titleUnderlineBlue: {
    width: 60,
    height: 3,
    backgroundColor: '#2563EB',
  },
  titleUnderlineDark: {
    flex: 1,
    height: 1.2,
    backgroundColor: '#1E293B',
  },
  paragraphContainer: {
    fontSize: 11.5,
    lineHeight: 1.60,
    color: '#475569',
    marginBottom: 16,
    textAlign: 'left',
  },
  riskRatingBanner: {
    padding: '12pt 16pt',
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    borderWidth: 1.2,
  },
  riskRatingLeft: {
    flexDirection: 'column',
    marginRight: 12,
    minWidth: 120,
  },
  riskOverallLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#B91C1C',
  },
  riskValueLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 2,
    color: '#B91C1C',
  },
  riskRatingRight: {
    flex: 1,
    fontSize: 10.0,
    color: '#475569',
    lineHeight: 1.45,
  },
  subSectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#2563EB',
    marginBottom: 10,
    marginTop: 4,
  },
  severityCardsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
    gap: 8,
  },
  severityCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: '10pt 4pt',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  severityCardNumber: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 3,
  },
  severityCardLabel: {
    fontSize: 7.5,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  chartContainerCard: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: '14pt 16pt',
    marginBottom: 20,
  },
  chartRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  chartLabel: {
    width: 60,
    fontSize: 8.5,
    fontWeight: 'bold',
    color: '#374151',
  },
  chartBarOuter: {
    flex: 1,
    height: 10,
    backgroundColor: '#F3F4F6',
    borderRadius: 5,
    marginRight: 10,
    overflow: 'hidden',
  },
  chartBarInner: {
    height: '100%',
    borderRadius: 5,
  },
  chartValue: {
    width: 15,
    fontSize: 8.5,
    textAlign: 'right',
    color: '#4B5563',
  },
  bulletRow: {
    flexDirection: 'row',
    marginBottom: 6,
    paddingLeft: 4,
  },
  bulletDot: {
    width: 10,
    fontSize: 11.5,
    color: '#4B5563',
  },
  bulletText: {
    flex: 1,
    fontSize: 11.5,
    color: '#475569',
    lineHeight: 1.60,
  },
});

function formatDate(dateStr: string | null | undefined, includeYear = false): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const monthsStandard = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthsStandard[date.getMonth()];
    const year = date.getFullYear();
    return includeYear ? `${day} ${month} ${year}` : `${day} ${month}`;
  } catch (e) {
    return '';
  }
}

function replacePlaceholders(html: string, reportData: ReportData, counts: Record<string, number>): string {
  if (!html) return '';
  const companyName = reportData.company_name || 'Client Organization';
  
  const getAutoPentestType = () => {
    const targets = reportData.target_details || [];
    if (targets.length === 0) return 'Web Application Penetration Test';
    const types = new Set<string>();
    targets.forEach((t: any) => {
      const typeStr = (t.target_type || t.type || '').toLowerCase();
      if (typeStr.includes('network') || typeStr.includes('infra')) types.add('Network Infrastructure');
      else if (typeStr.includes('api')) types.add('API');
      else if (typeStr.includes('mobile')) types.add('Mobile Application');
      else types.add('Web Application');
    });
    return `${Array.from(types).join(' & ')} Penetration Test`;
  };
  const testingType = (reportData as any).testing_type || getAutoPentestType();
  const totalFindings = counts.Critical + counts.High + counts.Medium + counts.Low + counts.Info;

  let targetName = 'the target applications';
  let hostText = '';
  if (Array.isArray(reportData.target_details) && reportData.target_details.length > 0) {
    const urls = reportData.target_details.map((t: any) => t.target_name || t.target_value || t.url).filter(Boolean);
    if (urls.length > 0) {
      let formattedTargets = '';
      if (urls.length === 1) {
        formattedTargets = urls[0];
      } else if (urls.length === 2) {
        formattedTargets = `${urls[0]} and ${urls[1]}`;
      } else {
        const remaining = urls.length - 2;
        formattedTargets = `${urls[0]}, ${urls[1]} and ${remaining} other${remaining > 1 ? 's' : ''}`;
      }
      targetName = formattedTargets;
      hostText = '';
    }
  }

  const startDateFormatted = reportData.start_date ? formatDate(reportData.start_date) : '';
  const endDateFormatted = reportData.end_date ? formatDate(reportData.end_date, true) : '';
  const dateRange = startDateFormatted && endDateFormatted 
    ? `from <strong>${startDateFormatted} to ${endDateFormatted}</strong>`
    : `during the designated testing window`;

  return html
    .replace(/{company_name}/g, companyName)
    .replace(/{testing_type}/g, testingType)
    .replace(/{target_name}/g, targetName)
    .replace(/{host_text}/g, hostText)
    .replace(/{date_range}/g, dateRange)
    .replace(/{total_findings}/g, String(totalFindings))
    .replace(/{critical_total}/g, String(counts.Critical))
    .replace(/{high_total}/g, String(counts.High))
    .replace(/{medium_total}/g, String(counts.Medium))
    .replace(/{low_total}/g, String(counts.Low))
    .replace(/{info_total}/g, String(counts.Info));
}

const ExecutiveSummaryPageV2: React.FC<ExecutiveSummaryProps> = ({ reportData, sectionId }) => {
  const companyName = reportData.company_name || 'Client Organization';
  
  const severities = ['Critical', 'High', 'Medium', 'Low', 'Info'] as const;
  const counts = severities.reduce((acc, sev) => {
    const directCount = (reportData.severity_counts as any)?.[sev] || 0;
    const calcCount = (reportData.open_close_counts_by_severity as any)?.[sev]?.Total || 0;
    acc[sev] = Math.max(directCount, calcCount);
    return acc;
  }, {} as Record<string, number>);

  let overallRisk = 'Info';
  if (counts.Critical > 0) overallRisk = 'Critical';
  else if (counts.High > 0) overallRisk = 'High';
  else if (counts.Medium > 0) overallRisk = 'Medium';
  else if (counts.Low > 0) overallRisk = 'Low';

  const configOverride = reportData.finding_config?.overallRiskOverride;
  const configDesc = reportData.finding_config?.overallRiskDescription;

  // Allow the editor to override the auto-computed risk level
  if (configOverride) {
    overallRisk = configOverride;
  } else if (reportData.overall_risk_override) {
    overallRisk = reportData.overall_risk_override;
  }

  let riskDescription = '';
  if (configDesc) {
    riskDescription = replacePlaceholders(configDesc, reportData, counts);
  } else if (reportData.overall_risk_description) {
    riskDescription = replacePlaceholders(reportData.overall_risk_description, reportData, counts);
  } else {
    // Dynamically build the description from the actual top-severity finding
    const allFindings: any[] = reportData.detailed_findings || [];
    const sevOrder: Record<string, number> = { critical: 1, high: 2, medium: 3, low: 4, info: 5 };
    const sortedFindings = [...allFindings].sort((a, b) => {
      const aS = (a.severity_category || a.severity || '').toLowerCase();
      const bS = (b.severity_category || b.severity || '').toLowerCase();
      return (sevOrder[aS] || 99) - (sevOrder[bS] || 99);
    });
    const topFinding = sortedFindings[0];
    const topTitle = topFinding ? (topFinding.title || '').trim() : '';
    const topCount = sortedFindings.filter((f: any) => {
      const s = (f.severity_category || f.severity || '').toLowerCase();
      return s === (topFinding ? (topFinding.severity_category || topFinding.severity || '').toLowerCase() : '');
    }).length;

    if (overallRisk === 'Critical' || overallRisk === 'High') {
      if (topTitle) {
        const article = /^[aeiou]/i.test(topTitle) ? 'an' : 'a';
        const plural = topCount > 1 ? ` and ${topCount - 1} other ${overallRisk.toLowerCase()}-severity issue${topCount > 2 ? 's' : ''}` : '';
        riskDescription = `${article} ${topTitle}${plural} ${topCount > 1 ? 'were' : 'was'} identified, posing a direct risk to the confidentiality, integrity, and availability of the application.`;
      } else {
        riskDescription = `${overallRisk.toLowerCase()}-severity vulnerabilities were identified, posing a direct risk to the confidentiality, integrity, and availability of the application.`;
      }
    } else if (overallRisk === 'Medium') {
      if (topTitle) {
        riskDescription = `${topTitle} and related medium-severity vulnerabilities were identified, exposing sensitive components to potential exploitation.`;
      } else {
        riskDescription = 'multiple medium-severity vulnerabilities were identified, exposing sensitive components to potential exploitation.';
      }
    } else if (overallRisk === 'Low') {
      riskDescription = 'only low-severity issues were found; no direct exploitation path to sensitive data was demonstrated during testing.';
    } else {
      riskDescription = 'only informational findings were observed; the application presented a low overall risk during the testing window.';
    }
  }


  const getRiskColor = (risk: string) => {
    if (risk === 'Critical' || risk === 'High') return '#B91C1C';
    if (risk === 'Medium') return '#EAB308';
    if (risk === 'Low') return '#16A34A';
    return '#64748B';
  };

  const getRiskBgColor = (risk: string) => {
    if (risk === 'Critical' || risk === 'High') return '#FEF2F2';
    if (risk === 'Medium') return '#FEFDF0';
    if (risk === 'Low') return '#F0FDF4';
    return '#F8FAFC';
  };

  const riskColor = getRiskColor(overallRisk);
  const riskBg = getRiskBgColor(overallRisk);

  const confidentialityText = `This document contains confidential information belonging to ${companyName}. It reflects a point-in-time assessment of the in-scope web application during the stated testing window and does not guarantee that all vulnerabilities were identified.`;

  // Raw default text or loaded executive summary html
  const rawSummaryHtml = reportData.executive_summary || 
    `<p>Capture The Bug was engaged by <strong>{company_name}</strong> to perform a <strong>{testing_type}</strong> of <strong>{target_name}</strong>{host_text}. Testing ran <strong>{date_range}</strong> using a grey-box approach with both authenticated (across 3 user roles) and unauthenticated perspectives, aligned to the <strong>OWASP Web Security Testing Guide (WSTG)</strong> and mapped to the <strong>OWASP Top 10 (2021)</strong>. The objective was to identify vulnerabilities that could compromise the confidentiality, integrity, or availability of the application and its data, and to provide prioritized remediation guidance.</p>`;

  const processedSummaryHtml = replacePlaceholders(rawSummaryHtml, reportData, counts);
  const summaryElements = parseHtmlToElementsSync(processedSummaryHtml);

  // Detect stale mock content so old reports regenerate dynamically
  const MOCK_MARKERS = ['SQL injection in product search', 'IDOR', 'TLS configuration and login rate-limiting were robust'];
  const isMockKeyFindings = (kf: string) => MOCK_MARKERS.some(m => kf.includes(m));

  // Build dynamic fallback bullets from Critical/High findings only (title only, no severity tag or ID)
  const buildDynamicBullets = (): string[] => {
    const allFindings: any[] = reportData.detailed_findings || [];
    const sevOrder: Record<string, number> = { critical: 1, high: 2 };
    const filtered = [...allFindings].filter((f: any) => {
      const s = (f.severity_category || f.severity || '').toLowerCase();
      return s === 'critical' || s === 'high';
    }).sort((a, b) => {
      const aS = (a.severity_category || a.severity || '').toLowerCase();
      const bS = (b.severity_category || b.severity || '').toLowerCase();
      return (sevOrder[aS] || 99) - (sevOrder[bS] || 99);
    });
    if (filtered.length === 0) return [];
    return filtered.map((f: any) => {
      const title = (f.title || 'Untitled finding').trim();
      return `${title}.`;
    });
  };

  let bullets: string[] = [];
  if (reportData.key_findings && !isMockKeyFindings(reportData.key_findings)) {
    const processedKeyFindingsHtml = replacePlaceholders(reportData.key_findings, reportData, counts);
    bullets = htmlToBullets(processedKeyFindingsHtml);
  } else {
    bullets = buildDynamicBullets();
  }


  const renderSummaryParagraph = (elements: PdfElement[]) => {
    return elements.map((el, index) => {
      if (el.type === 'paragraph') {
        return (
          <Text key={index} style={{ lineHeight: 1.60, color: '#475569', fontSize: 11.5, marginBottom: 12 }}>
            {el.children.map((child: any, childIdx: number) => {
              if (child.type === 'text') {
                const isBold = child.style && child.style.split(' ').includes('bold');
                return (
                  <Text key={childIdx} style={isBold ? { fontWeight: 'bold', color: '#1E293B' } : {}}>
                    {child.content}
                  </Text>
                );
              } else if (child.type === 'break') {
                return '\n';
              }
              return null;
            })}
          </Text>
        );
      } else if (el.type === 'text') {
        const isBold = el.style && el.style.split(' ').includes('bold');
        return (
          <Text key={index} style={[{ lineHeight: 1.60, color: '#475569', fontSize: 11.5, marginBottom: 12 }, isBold ? { fontWeight: 'bold', color: '#1E293B' } : {}]}>
            {el.content}
          </Text>
        );
      }
      return null;
    });
  };

  const maxVal = Math.max(...severities.map(s => counts[s]), 1);

  return (
    <Page size="A4" id={sectionId} style={styles.page}>
      <View style={styles.confidentialityBox}>
        <Text style={styles.confidentialityText}>{confidentialityText}</Text>
      </View>

      <Text style={styles.sectionTitle}>1. Executive Summary</Text>
      <View style={styles.titleUnderlineRow}>
        <View style={styles.titleUnderlineBlue} />
        <View style={styles.titleUnderlineDark} />
      </View>

      <View style={styles.paragraphContainer}>
        {renderSummaryParagraph(summaryElements)}
      </View>

      <View style={[styles.riskRatingBanner, { backgroundColor: riskBg, borderColor: riskColor }]}>
        <View style={styles.riskRatingLeft}>
          <Text style={[styles.riskOverallLabel, { color: riskColor }]}>OVERALL</Text>
          <Text style={[styles.riskValueLabel, { color: riskColor }]}>RISK: {overallRisk.toUpperCase()}</Text>
        </View>
        <Text style={styles.riskRatingRight}>
          - {riskDescription}
        </Text>
      </View>

      <Text style={styles.subSectionTitle}>Findings by severity</Text>

      <View style={styles.severityCardsRow}>
        {severities.map(sev => (
          <View 
            key={sev} 
            style={[
              styles.severityCard, 
              { borderTopWidth: 2.5, borderTopColor: severityColors[sev] }
            ]}
          >
            <Text style={[styles.severityCardNumber, { color: severityColors[sev] }]}>
              {counts[sev]}
            </Text>
            <Text style={styles.severityCardLabel}>
              {sev.toUpperCase()}
            </Text>
          </View>
        ))}
      </View>

      <View style={styles.chartContainerCard}>
        {severities.map((sev, idx) => {
          const filledWidth = maxVal > 0 ? (counts[sev] / maxVal) * 90 : 0;
          return (
            <View key={sev} style={[styles.chartRow, idx === 4 ? { marginBottom: 0 } : {}]}>
              <Text style={styles.chartLabel}>{sev}</Text>
              <View style={styles.chartBarOuter}>
                <View 
                  style={[
                    styles.chartBarInner, 
                    { 
                      width: `${Math.max(filledWidth, 1)}%`, 
                      backgroundColor: severityColors[sev] 
                    }
                  ]} 
                />
              </View>
              <Text style={styles.chartValue}>{counts[sev]}</Text>
            </View>
          );
        })}
      </View>

      {bullets.length > 0 && (
        <>
          <Text style={styles.subSectionTitle}>Key findings (bottom line up front)</Text>
          <View style={{ marginTop: 2 }}>
            {bullets.map((bullet, idx) => (
              <View key={idx} style={styles.bulletRow}>
                <Text style={styles.bulletDot}>•</Text>
                <Text style={styles.bulletText}>{bullet}</Text>
              </View>
            ))}
          </View>
        </>
      )}
      
      {/* Footer page number */}
      <View style={styles.footer} fixed>
        <Text style={styles.footerLeft}>Capture The Bug - Confidential</Text>
        <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
      </View>
    </Page>
  );
};

export default ExecutiveSummaryPageV2;
